import React, { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../ui/alert-dialog';
import { FlaskConical, Loader2 } from 'lucide-react';
import { createAiqTestInvoice } from '../../lib/api';
import { errMsg, when } from './aiqMeta';

const CodeInput = ({ id, label, value, onChange, options, testId }) => (
  <div className="flex-1 min-w-[180px]">
    <Label htmlFor={id} className="text-xs">{label}</Label>
    <Input id={id} list={`${id}-list`} value={value} onChange={(e) => onChange(e.target.value)} className="font-mono" data-testid={testId} />
    <datalist id={`${id}-list`}>{(options || []).map((c) => <option key={c} value={c} />)}</datalist>
  </div>
);

const LastTest = ({ last }) => last && (
  <div className="text-xs rounded border border-zinc-200 bg-zinc-50 px-3 py-2" data-testid="aiq-test-invoice-last">
    Last test: draft <span className="font-mono font-semibold">#{last.invoice_id}</span> · ref <span className="font-mono">{last.external_reference}</span> · {last.customer_code} / {last.stock_code} · {last.created_by} · {when(last.created_at)}.
    <span className="text-amber-800"> Remember to delete it in AccountsIQ.</span>
  </div>
);

export const TestInvoiceCard = ({ sample, last, onCreated }) => {
  const [customer, setCustomer] = useState('');
  const [stock, setStock] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    setCustomer((c) => c || sample?.customer_codes?.[0] || '');
    setStock((s) => s || sample?.stock_codes?.[0] || '');
  }, [sample]);

  const create = async () => {
    setConfirm(false);
    setBusy(true);
    try {
      const { data } = await createAiqTestInvoice({ customer_code: customer.trim(), stock_code: stock.trim() });
      toast.success(`Test draft invoice #${data.invoice_id} created in AccountsIQ`);
      onCreated?.(data);
    } catch (e) {
      toast.error(errMsg(e, 'Could not create the test draft invoice'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="rounded-lg border border-amber-200 bg-amber-50/30 p-4 space-y-3" data-testid="aiq-test-invoice-card">
      <div>
        <div className="text-sm font-semibold flex items-center gap-2"><FlaskConical className="w-4 h-4 text-amber-700" />Test draft invoice (optional)</div>
        <p className="text-xs text-zinc-600 mt-0.5 max-w-2xl">Creates <strong>one real draft invoice</strong> in AccountsIQ: 1 × the stock item at 0.00, marked as a connection test. It proves invoices actually land. Delete it in AccountsIQ afterwards.</p>
      </div>
      <div className="flex flex-wrap items-end gap-3">
        <CodeInput id="aiq-test-customer" label="AccountsIQ customer code" value={customer} onChange={setCustomer} options={sample?.customer_codes} testId="aiq-test-invoice-customer" />
        <CodeInput id="aiq-test-stock" label="AccountsIQ stock code" value={stock} onChange={setStock} options={sample?.stock_codes} testId="aiq-test-invoice-stock" />
        <Button variant="outline" onClick={() => setConfirm(true)} disabled={busy || !customer.trim() || !stock.trim()} className="border-amber-300" data-testid="aiq-test-invoice-btn">
          {busy ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : <FlaskConical className="w-4 h-4 mr-1.5" />}
          Create test draft invoice
        </Button>
      </div>
      <LastTest last={last} />
      <AlertDialog open={confirm} onOpenChange={setConfirm}>
        <AlertDialogContent data-testid="aiq-test-invoice-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Create a real draft invoice in AccountsIQ?</AlertDialogTitle>
            <AlertDialogDescription>
              This saves one zero-value draft for customer <strong>{customer}</strong> with stock item <strong>{stock}</strong>. It stays in AccountsIQ until you delete it there.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="aiq-test-invoice-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={create} className="bg-amber-600 hover:bg-amber-700" data-testid="aiq-test-invoice-confirm-btn">Create draft</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
